<template>
  <div>
    <div class="white-bg p3 m3">
      <ProfileForm
        v-model="data"
        :account-deletion-allowed="accountDeletionAllowed"
        :account-deletion-check-loading="accountDeletionCheckLoading"
        @save-form="updateUserData"
        @reset-form="resetForm"
        @show-delete-account-modal="openDeleteAccountModal()"
      />
      <div v-for="(externalContext, index) in externalContexts" :key="externalContext.partner" class="pt3">
        <hr class="m0 dark-grey">
        <ExternalContextsDataForm
          class="pt3"
          :index="index"
          :external-context="externalContext"
        />
      </div>
    </div>
    <Modal v-if="showDeleteModal" @close="closeDeleteAccountModal">
      <template #body>
        <div style="max-width: 800px; max-height: 90vh; overflow-y: auto" class="col-12 flex flex-column items-center white-bg p3">
          <div v-if="accountDeletionAllowed">
            <div class="mb3">
              <div class="h5n" v-html="getReferencelabel('DELETE_ACCOUNT_WARNING')"></div>
            </div>
            <SimpleField
              id="deleteText"
              class="col-12 py1 mb3"
              autocomplete="off"
              :value="deleteText"
              type="text"
              :placeholder="getReferencelabel('DELETE')"
              @update:value="deleteText = $event"
            />
            <div class="flex col-12 gap1">
              <ButtonV2 :reference="'CANCEL'" rounded class="px2 py1 col-6" color="light" :action="closeDeleteAccountModal" />
              <ButtonV2 :reference="'DELETE'" rounded class="px2 py1 col-6" color="red" :action="confirmDelete" :disabled="disableDeleteButton" />
            </div>
          </div>
          <div v-else>
            <div class="h5n" v-html="getReferencelabel('DELETE_ACCOUNT_NOT_ALLOWED_WARNING')"></div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

import { format } from 'date-fns';
import { updateUser, anonymiseUser } from '@/mixins/requests';
import { isInImpersonateModeNotification, errorNotification, getReferencelabel, logout } from '@/mixins/utils';

import ProfileForm from '@/components/forms/ProfileForm';
import ExternalContextsDataForm from '@/components/forms/ExternalContextsDataForm';
import SimpleField from '@/components/inputs/SimpleField';
import Modal from '@/components/blocks/Modal';
import ButtonV2 from '@/components/elements/ButtonV2.vue';
import { getOrders } from '../../mixins/requests';

export default {
  components: { ProfileForm, ExternalContextsDataForm, SimpleField, Modal, ButtonV2 },
  setup () {
    const store = useStore();

    const data = ref(JSON.parse(JSON.stringify(store.state.user)));

    const showDeleteModal = ref(false);

    const deleteText = ref('');

    const disableDeleteButton = computed(() => deleteText.value !== getReferencelabel('DELETE'));

    const externalContexts = computed(() => {
      const { externalContextsData } = store.state.user;
      if (!externalContextsData || !Object.keys(externalContextsData).length) {
        return [];
      }

      return Object.keys(externalContextsData).filter(key => !!externalContextsData[key]).map(key => ({
        partner: key,
        user: { ...store.state.user, ...externalContextsData[key] }
      }));
    });

    const accountDeletionAllowed = ref(null);
    const accountDeletionCheckLoading = ref(true);

    async function updateUserData () {
      if (isInImpersonateModeNotification()) {
        return;
      }

      try {
        store.commit('setLoading', true);

        const user = await updateUser(data.value);

        if (!user?.success) {
          throw new Error(user?.message);
        }
      } catch (error) {
        console.error('Error updating user:', error);
        errorNotification('UPDATE_USER_ERROR_NOTIFICATION');
      }

      store.commit('setLoading', false);
    }

    function resetForm () {
      Object.assign(data.value, JSON.parse(JSON.stringify(store.state.user)));
    }

    function openDeleteAccountModal () {
      if (isInImpersonateModeNotification()) {
        return;
      }

      deleteText.value = '';
      showDeleteModal.value = true;
    }

    function closeDeleteAccountModal () {
      showDeleteModal.value = false;
    }

    async function confirmDelete () {
      if (isInImpersonateModeNotification()) {
        return;
      }

      if (deleteText.value !== getReferencelabel('DELETE')) {
        return;
      }

      closeDeleteAccountModal();

      try {
        store.commit('setLoading', true);

        const anonymise = await anonymiseUser();

        if (!anonymise?.success) {
          errorNotification('DELETE_USER_ERROR_NOTIFICATION');
          throw new Error(anonymise?.message);
        }

        logout();
      } catch (error) {
        console.error('Error anonymising user:', error);
      }

      store.commit('setLoading', false);
    }

    async function checkAccountDeletionAllowed () {
      const today = format(new Date(), 'dd/MM/yyyy');
      const orders = await getOrders(0, 9999999, null, today);

      // If there are outstanding bookings (not Annulé) in the future, we can't delete the account
      return !(orders.count > 0 && orders.items.some(order => order.status !== 'Annulé'));
    }

    onMounted(async () => {
      try {
        accountDeletionAllowed.value = await checkAccountDeletionAllowed();
      } catch (error) {
        console.error('Error checking account deletion eligibility:', error);
        accountDeletionAllowed.value = false;
      } finally {
        accountDeletionCheckLoading.value = false;
      }
    });

    return {
      data,
      updateUserData,
      resetForm,
      externalContexts,
      getReferencelabel,
      showDeleteModal,
      closeDeleteAccountModal,
      openDeleteAccountModal,
      disableDeleteButton,
      deleteText,
      confirmDelete,
      accountDeletionAllowed,
      accountDeletionCheckLoading
    };
  }
};
</script>
